import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, TimelineItem } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PlusCircle, ClipboardList, ExternalLink, Check, Clock, Trash2, FileText, Paperclip, Users } from 'lucide-react';
import { fmtDate, fmtDateTime } from '@/lib/format';
import AIEmailPanel from '@/components/AIEmailPanel';
import FileUploader from '@/components/FileUploader';

function NewOnboardingDialog({ companyId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', role: '', start_date: '', next_followup_date: '' });
  const submit = async () => {
    if (!f.name || !f.email || !f.role) return toast.error('Name, email and role are required');
    try {
      await api.post('/onboardings', { ...f, company_id: companyId });
      toast.success('Onboarding created'); setOpen(false); onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-onboarding-button"><PlusCircle className="h-4 w-4 mr-2" />Add onboarding</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add onboarding</DialogTitle><DialogDescription>Manually add someone joining outside the recruitment pipeline.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5"><Label>Full name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="onboarding-name" /></div>
          <div className="space-y-1.5"><Label>Email *</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Role *</Label><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Joining date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Next follow-up</Label><Input type="date" value={f.next_followup_date} onChange={(e) => setF({ ...f, next_followup_date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnboardingDrawer({ id, companyId, onClose, onChanged }) {
  const [ob, setOb] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [files, setFiles] = useState([]);
  const [letterKind, setLetterKind] = useState('offer');
  const [drafting, setDrafting] = useState(false);

  const load = () => api.get(`/onboardings/${id}`).then((r) => setOb(r.data));
  useEffect(() => {
    if (id) {
      load();
      api.get(`/templates?company_id=${companyId}&category=onboarding`).then((r) => setTemplates(r.data));
      api.get(`/files?entity_type=onboarding&entity_id=${id}`).then((r) => setFiles(r.data));
    }
  }, [id, companyId]);

  const toggleItem = async (item_id, completed) => {
    try { const r = await api.post(`/onboardings/${id}/checklist/toggle`, { item_id, completed }); setOb(r.data); onChanged && onChanged(); } catch (e) { toast.error('Failed'); }
  };

  const patch = async (fields) => {
    try { const r = await api.patch(`/onboardings/${id}`, fields); setOb(r.data); onChanged && onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const draftLetter = async () => {
    setDrafting(true);
    try {
      const company = await api.get(`/companies/${companyId}`);
      const r = await api.post('/ai/draft-letter', {
        kind: letterKind, employee_name: ob.name, role: ob.role, company_name: company.data.name, start_date: ob.start_date,
      });
      const win = window.open('', '_blank');
      win.document.write(`<pre style="font-family: -apple-system, system-ui; padding: 32px; white-space: pre-wrap; line-height: 1.6">${r.data.content}</pre>`);
      win.document.title = `${letterKind} letter - ${ob.name}`;
    } catch (e) { toast.error('Failed to draft'); }
    finally { setDrafting(false); }
  };

  if (!ob) return null;
  const progress = ob.checklist ? ob.checklist.filter((c) => c.completed).length : 0;
  const total = (ob.checklist || []).length;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="onboarding-drawer">
        <SheetHeader>
          <SheetTitle className="font-display">{ob.name}</SheetTitle>
          <SheetDescription>{ob.role} · {ob.email}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Progress</p>
                <p className="text-sm font-medium tabular-nums">{progress}/{total}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label className="text-xs">Joining date</Label><Input type="date" value={ob.start_date || ''} onChange={(e) => patch({ start_date: e.target.value })} data-testid="onboarding-start-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Next follow-up</Label><Input type="date" value={ob.next_followup_date || ''} onChange={(e) => patch({ next_followup_date: e.target.value })} data-testid="onboarding-next-followup" /></div>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <h4 className="font-display font-semibold mb-3">Checklist</h4>
              <ul className="space-y-2">
                {(ob.checklist || []).map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <Checkbox checked={item.completed} onCheckedChange={(v) => toggleItem(item.id, !!v)} data-testid={`checklist-item-${item.id}`} className="mt-0.5" />
                    <div className="flex-1">
                      <p className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>{item.label}</p>
                      {item.completed && item.completed_at && (
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Check className="h-3 w-3" /> {fmtDateTime(item.completed_at)} by {item.actor_name}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <div className="flex items-center justify-between mb-2"><h4 className="font-display font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Documents</h4></div>
              <div className="flex items-center gap-2 flex-wrap">
                {['offer','experience','relieving','no_dues'].map((k) => (
                  <Button key={k} size="sm" variant={letterKind === k ? 'default' : 'outline'} onClick={() => setLetterKind(k)} data-testid={`letter-kind-${k}`} className="capitalize">{k.replace('_',' ')}</Button>
                ))}
                <Button size="sm" onClick={draftLetter} disabled={drafting} data-testid="draft-letter-button"><ExternalLink className="h-3.5 w-3.5 mr-1" />Draft & open</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">AI drafts a formal letter you can copy or print.</p>
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-semibold flex items-center gap-1 mb-2"><Paperclip className="h-3 w-3" /> Attached documents</p>
                <FileUploader entityType="onboarding" entityId={ob.id} label="document" files={files} onUploaded={(f) => setFiles([f, ...files])} onDeleted={(fid) => setFiles(files.filter((x) => x.id !== fid))} buttonText="Upload document" />
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <h4 className="font-display font-semibold mb-3">Timeline (timestamped)</h4>
              <div className="relative timeline-rail pl-1">
                {(ob.timeline || []).slice().reverse().map((ev) => <TimelineItem key={ev.id} event={ev} />)}
              </div>
            </div>
          </div>
          <div>
            <AIEmailPanel
              onboarding={{ ...ob }}
              companyId={companyId}
              entityType="onboarding"
              entityId={ob.id}
              templates={templates}
              onSent={load}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function OnboardingPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [obs, setObs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState('recent');

  const load = () => api.get(`/onboardings?company_id=${companyId}&view=${view}`).then((r) => setObs(r.data));
  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); }, [companyId]);
  useEffect(() => { load(); }, [companyId, view]);
  useEffect(() => { const q = params.get('open'); if (q) { setOpenId(q); params.delete('open'); setParams(params); } }, [params]);

  const del = async (id) => { if (!window.confirm('Delete onboarding?')) return; await api.delete(`/onboardings/${id}`); toast.success('Deleted'); load(); };

  const emptyDescriptor = {
    recent: 'Newly onboarded joiners appear here for the first 30 days. Once a candidate reaches the Hired stage, their onboarding record shows here automatically.',
    active: 'Employees older than 30 days show up here. This is your quiet, long-term roster.',
    all: 'Once a candidate reaches the Hired stage, their onboarding record appears here automatically.',
  }[view];

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Onboarding`}
        description="Every step timestamped. Recent joiners (last 30 days) move automatically into Active Employees."
        actions={<NewOnboardingDialog companyId={companyId} onCreated={load} />}
      >
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="onboarding-view-tabs">
            <TabsTrigger value="recent" data-testid="tab-recent"><Users className="h-3.5 w-3.5 mr-1" />Recent (30 days)</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active"><ClipboardList className="h-3.5 w-3.5 mr-1" />Active employees</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {obs.length === 0 ? (
          <EmptyState icon={ClipboardList} title={view === 'active' ? 'No active employees yet' : view === 'recent' ? 'No recent onboardings' : 'No onboardings yet'} description={emptyDescriptor} />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joining</TableHead>
                <TableHead>Follow-up</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Employment</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {obs.map((o) => {
                  const done = (o.checklist || []).filter((i) => i.completed).length;
                  const total = (o.checklist || []).length;
                  const isRecent = o.employee_status === 'recent';
                  return (
                    <TableRow key={o.id} className="cursor-pointer" onClick={() => setOpenId(o.id)} data-testid={`onboarding-row-${o.id}`}>
                      <TableCell><p className="font-medium">{o.name}</p><p className="text-xs text-muted-foreground">{o.email}</p></TableCell>
                      <TableCell>{o.role}</TableCell>
                      <TableCell>{fmtDate(o.start_date)}</TableCell>
                      <TableCell className={(() => { const t = o.next_followup_date && o.next_followup_date < new Date().toISOString().slice(0,10) ? 'text-[hsl(var(--danger))] font-medium' : ''; return t; })()}>{fmtDate(o.next_followup_date)}</TableCell>
                      <TableCell><span className="tabular-nums text-sm">{done}/{total}</span></TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded ${o.status === 'completed' ? 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]' : 'bg-muted text-muted-foreground'}`}>{o.status}</span></TableCell>
                      <TableCell>
                        <Badge variant="outline" className={isRecent ? 'border-primary/40 text-primary' : ''}>{isRecent ? 'Recent' : 'Active'}</Badge>
                      </TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); del(o.id); }} data-testid={`onboarding-delete-${o.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {openId && <OnboardingDrawer id={openId} companyId={companyId} onClose={() => setOpenId(null)} onChanged={load} />}
    </>
  );
}
